import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import "../../../css/Dashboard.css";

const toDateString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatDay = dateString => new Intl.DateTimeFormat("pl-PL", {
  weekday: "long",
  day: "2-digit",
  month: "long"
}).format(new Date(`${dateString}T12:00:00`));

const formatTime = value => value ? value.slice(11, 16) : "—";
const formatDate = value => value ? new Intl.DateTimeFormat("pl-PL", {day: "2-digit", month: "2-digit", year: "numeric"}).format(new Date(`${value}T12:00:00`)) : "—";
const formatDaysRemaining = value => {
  const days = Number(value);
  if (!Number.isFinite(days)) return "";
  if (days === 0) return "dzisiaj";
  if (days === 1) return "za 1 dzień";
  return `za ${days} dni`;
};
const number = value => Number(value || 0);
const formatNumber = (value, digits = 1) => number(value).toLocaleString("pl-PL", {maximumFractionDigits: digits});
const money = value => value == null ? "—" : number(value).toLocaleString("pl-PL", {style: "currency", currency: "PLN", maximumFractionDigits: 0});
const percent = value => value == null ? "—" : `${formatNumber(value, 1)}%`;

const QuickAction = ({icon, title, description, onClick, accent = "purple"}) => (
  <button type="button" className={`dashboard-action dashboard-action-${accent}`} onClick={onClick}>
    <span className="dashboard-action-icon"><i className={icon}></i></span>
    <span className="dashboard-action-copy">
      <strong>{title}</strong>
      <small>{description}</small>
    </span>
    <i className="fas fa-chevron-right dashboard-action-arrow"></i>
  </button>
);

const Kpi = ({label, value, detail, icon, tone = "purple"}) => (
  <div className={`dashboard-kpi dashboard-kpi-${tone}`}>
    <span className="dashboard-kpi-icon"><i className={icon}></i></span>
    <div>
      <small>{label}</small>
      <strong>{value}</strong>
      <span>{detail}</span>
    </div>
  </div>
);

const ProgressLine = ({label, actual, planned, suffix = "", inverse = false}) => {
  const p = number(planned);
  const a = number(actual);
  const ratio = p > 0 ? a / p : 0;
  const width = Math.max(0, Math.min(100, ratio * 100));
  const tone = p <= 0 ? "neutral" : inverse ? (ratio <= 1 ? "good" : "bad") : (ratio >= 1 ? "good" : ratio >= 0.8 ? "warning" : "bad");

  return (
    <div className="dashboard-progress-row">
      <div className="dashboard-progress-head">
        <span>{label}</span>
        <strong>{p > 0 ? `${formatNumber(a)}${suffix} / ${formatNumber(p)}${suffix}` : "Brak planu"}</strong>
      </div>
      <div className="dashboard-progress-track">
        <div className={`dashboard-progress-bar dashboard-progress-${tone}`} style={{width: `${p > 0 ? width : 0}%`}}></div>
      </div>
    </div>
  );
};

const aggregateDay = (reports, date) => {
  const rows = reports
    .map(report => ({report, day: (report.days || []).find(item => item.date === date)}))
    .filter(item => item.day);

  const sum = field => rows.reduce((total, item) => total + number(item.day[field]), 0);
  const plannedSales = sum("plannedSales");
  const actualSales = sum("actualSales");
  const plannedHours = sum("plannedHours");
  const actualHours = sum("actualHours");

  const weighted = (field, salesField) => {
    const sales = rows.reduce((total, item) => total + number(item.day[salesField]), 0);
    if (sales <= 0) return null;
    const total = rows.reduce((result, item) => result + number(item.day[field]) * number(item.day[salesField]), 0);
    return total / sales;
  };

  return {
    rows,
    demandCount: sum("demandCount"),
    assignedCount: sum("assignedCount"),
    openShiftCount: sum("openShiftCount"),
    requestCount: sum("requestCount"),
    plannedHours,
    actualHours,
    plannedSales,
    actualSales,
    spmhPlan: plannedHours > 0 ? plannedSales / plannedHours : null,
    spmhActual: actualHours > 0 ? actualSales / actualHours : null,
    colPlanPercent: weighted("colPlanPercent", "plannedSales"),
    colActualPercent: weighted("colActualPercent", "actualSales")
  };
};

const DayPlan = ({title, date, dayData, compactActual = false}) => (
  <div className="dashboard-day-card">
    <div className="dashboard-day-heading">
      <div>
        <span>{title}</span>
        <strong>{formatDay(date)}</strong>
      </div>
      <span className={`dashboard-day-status ${dayData.openShiftCount > 0 ? "danger" : dayData.demandCount > 0 ? "ok" : "neutral"}`}>
        {dayData.openShiftCount > 0 ? `${dayData.openShiftCount} braków obsady` : dayData.demandCount > 0 ? "Obsada kompletna" : "Brak zaplanowanych zmian"}
      </span>
    </div>

    <div className="dashboard-day-metrics">
      <div><small>Obsada</small><strong>{dayData.assignedCount} / {dayData.demandCount}</strong><span>osób / zmian</span></div>
      <div><small>Godziny plan</small><strong>{formatNumber(dayData.plannedHours)} h</strong><span>{compactActual ? "plan na jutro" : `wykonanie ${formatNumber(dayData.actualHours)} h`}</span></div>
      <div><small>Sprzedaż plan</small><strong>{money(dayData.plannedSales)}</strong><span>{compactActual ? "plan na jutro" : `wykonanie ${money(dayData.actualSales)}`}</span></div>
      <div><small>SPMH</small><strong>{dayData.spmhPlan == null ? "—" : `${formatNumber(dayData.spmhPlan)} zł/h`}</strong><span>{compactActual ? "plan" : `wykonanie ${dayData.spmhActual == null ? "—" : `${formatNumber(dayData.spmhActual)} zł/h`}`}</span></div>
      <div><small>%COL</small><strong>{percent(dayData.colPlanPercent)}</strong><span>{compactActual ? "plan" : `wykonanie ${percent(dayData.colActualPercent)}`}</span></div>
      <div><small>Wnioski</small><strong>{dayData.requestCount}</strong><span>dotyczących dnia</span></div>
    </div>
  </div>
);

const DepartmentDayTable = ({reports, today, tomorrow}) => (
  <div className="dashboard-table-wrap">
    <table className="dashboard-table">
      <thead>
        <tr>
          <th>Dział</th>
          <th>Dzisiaj</th>
          <th>Godziny</th>
          <th>Sprzedaż</th>
          <th>SPMH / COL</th>
          <th>Jutro</th>
          <th>Godziny jutro</th>
        </tr>
      </thead>
      <tbody>
        {reports.map(report => {
          const todayRow = (report.days || []).find(day => day.date === today);
          const tomorrowRow = (report.days || []).find(day => day.date === tomorrow);
          const todayOpen = number(todayRow?.openShiftCount);
          const tomorrowOpen = number(tomorrowRow?.openShiftCount);
          return (
            <tr key={report.departmentId}>
              <td><strong>{report.departmentName}</strong></td>
              <td>
                <span className={`dashboard-status-tag ${todayOpen > 0 ? "danger" : todayRow?.demandCount ? "ok" : "neutral"}`}>
                  {todayRow?.demandCount ? `${number(todayRow.assignedCount)}/${number(todayRow.demandCount)}${todayOpen > 0 ? ` · brak ${todayOpen}` : ""}` : "brak planu"}
                </span>
              </td>
              <td>{todayRow ? `${formatNumber(todayRow.actualHours)} / ${formatNumber(todayRow.plannedHours)} h` : "—"}</td>
              <td>{todayRow ? `${money(todayRow.actualSales)} / ${money(todayRow.plannedSales)}` : "—"}</td>
              <td>{todayRow ? `${todayRow.spmhActual == null ? "—" : `${formatNumber(todayRow.spmhActual)} zł/h`} · ${percent(todayRow.colActualPercent)}` : "—"}</td>
              <td>
                <span className={`dashboard-status-tag ${tomorrowOpen > 0 ? "danger" : tomorrowRow?.demandCount ? "ok" : "neutral"}`}>
                  {tomorrowRow?.demandCount ? `${number(tomorrowRow.assignedCount)}/${number(tomorrowRow.demandCount)}${tomorrowOpen > 0 ? ` · brak ${tomorrowOpen}` : ""}` : "brak planu"}
                </span>
              </td>
              <td>{tomorrowRow ? `${formatNumber(tomorrowRow.plannedHours)} h` : "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const ManagerDashboard = ({isAdmin}) => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [reports, setReports] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("ALL");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState("");
  const [contractAlerts, setContractAlerts] = useState([]);
  const [unexcusedAbsenceAlerts, setUnexcusedAbsenceAlerts] = useState([]);

  const today = useMemo(() => toDateString(new Date()), []);
  const tomorrow = useMemo(() => toDateString(addDays(new Date(), 1)), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [departmentsResponse, contractAlertsResponse, unexcusedAbsenceAlertsResponse] = await Promise.all([
        axiosPrivate.get("/schedule-reports/departments"),
        axiosPrivate.get("/employees/contract-alerts"),
        axiosPrivate.get("/employee-absences/unexcused-alerts")
      ]);
      const availableDepartments = departmentsResponse.data || [];
      setDepartments(availableDepartments);
      setContractAlerts(contractAlertsResponse.data || []);
      setUnexcusedAbsenceAlerts(unexcusedAbsenceAlertsResponse.data || []);

      const result = await Promise.allSettled(availableDepartments.map(department =>
        axiosPrivate.get("/schedule-reports/overview", {
          params: {departmentId: department.id, dateFrom: today, dateTo: tomorrow}
        })
      ));

      const loadedReports = result
        .filter(item => item.status === "fulfilled" && item.value?.data)
        .map(item => item.value.data)
        .sort((a, b) => String(a.departmentName || "").localeCompare(String(b.departmentName || ""), "pl"));

      setReports(loadedReports);
      if (availableDepartments.length > 0 && loadedReports.length === 0) {
        setError("Nie udało się pobrać danych operacyjnych działów.");
      }
      setLastRefresh(new Date());
    } catch (err) {
      setDepartments([]);
      setReports([]);
      setContractAlerts([]);
      setUnexcusedAbsenceAlerts([]);
      setError(err?.response?.data?.message || "Nie udało się pobrać danych dashboardu.");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, today, tomorrow]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleReports = useMemo(
    () => departmentFilter === "ALL" ? reports : reports.filter(report => String(report.departmentId) === String(departmentFilter)),
    [reports, departmentFilter]
  );

  const todayData = useMemo(() => aggregateDay(visibleReports, today), [visibleReports, today]);
  const tomorrowData = useMemo(() => aggregateDay(visibleReports, tomorrow), [visibleReports, tomorrow]);

  const pendingRequests = visibleReports.reduce((total, report) => total + number(report.summary?.pendingRequestCount), 0);
  const lateCount = visibleReports.reduce((total, report) => total + number(report.summary?.lateCount), 0);
  const missingRcpCount = visibleReports.reduce((total, report) => total + number(report.summary?.missingRcpCount), 0);
  const selectedDepartmentName = departmentFilter === "ALL" ? null : departments.find(item => String(item.id) === String(departmentFilter))?.name;
  const visibleContractAlerts = selectedDepartmentName
    ? contractAlerts.filter(alert => (alert.departmentNames || []).includes(selectedDepartmentName))
    : contractAlerts;
  const noContractAlerts = visibleContractAlerts.filter(alert => alert.status === "NO_CONTRACT");
  const expiringContractAlerts = visibleContractAlerts
    .filter(alert => alert.status === "EXPIRING")
    .sort((a, b) => String(a.dateTo || "9999-12-31").localeCompare(String(b.dateTo || "9999-12-31")));
  const noContractCount = noContractAlerts.length;
  const expiringContractCount = expiringContractAlerts.length;
  const firstEmployeeWithoutContract = noContractAlerts[0];
  const nearestExpiringContract = expiringContractAlerts[0];
  const visibleUnexcusedAbsenceAlerts = selectedDepartmentName
    ? unexcusedAbsenceAlerts.filter(alert => (alert.departmentNames || []).includes(selectedDepartmentName))
    : unexcusedAbsenceAlerts;
  const unexcusedAbsenceAlertCount = visibleUnexcusedAbsenceAlerts.length;
  const highestUnexcusedAbsenceAlert = visibleUnexcusedAbsenceAlerts[0];

  const attentionItems = [];
  if (todayData.openShiftCount > 0) attentionItems.push({tone: "danger", icon: "fas fa-user-times", text: `Dzisiaj brakuje obsady na ${todayData.openShiftCount} zmianach.`, path: "/app/scheduler"});
  if (tomorrowData.openShiftCount > 0) attentionItems.push({tone: "warning", icon: "fas fa-user-clock", text: `Na jutro pozostaje ${tomorrowData.openShiftCount} nieobsadzonych zmian.`, path: "/app/scheduler"});
  if (pendingRequests > 0) attentionItems.push({tone: "warning", icon: "fas fa-inbox", text: `${pendingRequests} wniosków czeka na decyzję.`, path: "/app/schedule-requests"});
  if (missingRcpCount > 0) attentionItems.push({tone: "danger", icon: "fas fa-clock", text: `${missingRcpCount} zmian ma brakujące lub niepełne RCP.`, path: "/app/rcp-events"});
  if (lateCount > 0) attentionItems.push({tone: "neutral", icon: "fas fa-stopwatch", text: `W bieżącym zakresie odnotowano ${lateCount} spóźnień.`, path: "/app/schedule-reports"});
  if (unexcusedAbsenceAlertCount > 0) {
    const strongest = highestUnexcusedAbsenceAlert;
    const threshold = strongest?.alertThreshold || 2;
    const example = strongest?.employeeName
      ? ` · ${strongest.employeeName}: ${strongest.unexcusedAbsenceCount} NN${strongest.latestAbsenceDate ? `, ostatnia ${formatDate(strongest.latestAbsenceDate)}` : ""}`
      : "";
    attentionItems.push({
      tone: "danger",
      icon: "fas fa-user-times",
      text: `${unexcusedAbsenceAlertCount} ${unexcusedAbsenceAlertCount === 1 ? "pracownik osiągnął" : "pracowników osiągnęło"} próg ${threshold} nieusprawiedliwionych nieobecności${example}.`,
      path: "/app/employees"
    });
  }
  if (noContractCount > 0) {
    const example = firstEmployeeWithoutContract?.employeeName ? ` · np. ${firstEmployeeWithoutContract.employeeName}` : "";
    attentionItems.push({
      tone: "danger",
      icon: "fas fa-file-circle-xmark",
      text: `${noContractCount} ${noContractCount === 1 ? "pracownik nie ma" : "pracowników nie ma"} aktywnej umowy${example}.`,
      path: "/app/employees"
    });
  }
  if (expiringContractCount > 0) {
    const nearest = nearestExpiringContract
      ? ` · najbliższa: ${nearestExpiringContract.employeeName || "pracownik"}${nearestExpiringContract.contractIdentifier ? ` (${nearestExpiringContract.contractIdentifier})` : ""} — ${formatDate(nearestExpiringContract.dateTo)}${nearestExpiringContract.daysRemaining != null ? `, ${formatDaysRemaining(nearestExpiringContract.daysRemaining)}` : ""}`
      : "";
    attentionItems.push({
      tone: "warning",
      icon: "fas fa-file-contract",
      text: `${expiringContractCount} ${expiringContractCount === 1 ? "umowa zbliża się" : "umów zbliża się"} do końca${nearest}.`,
      path: "/app/employees"
    });
  }

  const actions = [
    {icon: "fas fa-calendar-alt", title: "Grafik", description: "Tworzenie, obsada i publikacja grafików", path: "/app/scheduler"},
    {icon: "fas fa-clipboard-list", title: "Plany zapotrzebowania", description: "Zapotrzebowanie na ludzi i stanowiska", path: "/app/resource-plan"},
    {icon: "fas fa-inbox", title: "Wnioski", description: "Urlopy, wolne, zamiany i otwarte zmiany", path: "/app/schedule-requests"},
    {icon: "fas fa-calendar-check", title: "Dostępność", description: "Dyspozycyjność i sezonowość pracowników", path: "/app/employee-availability-schedule"},
    {icon: "fas fa-clock", title: "Rejestr RCP", description: "Wejścia, wyjścia i rzeczywisty czas pracy", path: "/app/rcp-events"},
    {icon: "fas fa-umbrella-beach", title: "Urlopy działów", description: "Widok zbiorczy i limity nieobecności", path: "/app/department-vacations"},
    {icon: "fas fa-chart-line", title: "Raporty", description: "Koszty, godziny, sprzedaż, SPMH i COL", path: "/app/schedule-reports"},
    {icon: "fas fa-graduation-cap", title: "Testy szkoleniowe", description: "Wyniki testów wpływające na dopuszczenie do grafiku", path: "/app/trainingx"},
    ...(isAdmin ? [
      {icon: "fas fa-users", title: "Pracownicy", description: "Kartoteki, stanowiska i kwalifikacje", path: "/app/employees"},
      {icon: "fas fa-file-contract", title: "Umowy", description: "Umowy i warunki zatrudnienia", path: "/app/contract"},
      {icon: "fas fa-cogs", title: "Ustawienia", description: "Konfiguracja aplikacji i firmy", path: "/app/settings"}
    ] : [])
  ];

  if (loading) {
    return <div className="dashboard-loading"><div className="dashboard-spinner"></div><span>Pobieranie aktualnej sytuacji…</span></div>;
  }

  return (
    <>
      <div className="dashboard-toolbar">
        <div>
          <span className="dashboard-eyebrow">Centrum operacyjne</span>
          <h1>{isAdmin ? "Dashboard administratora" : "Dashboard kierownika"}</h1>
          <p>Najważniejsze informacje o grafikach, obsadzie i realizacji planu na dziś i jutro.</p>
        </div>
        <div className="dashboard-toolbar-actions">
          <label>
            <span>Dział</span>
            <select value={departmentFilter} onChange={event => setDepartmentFilter(event.target.value)}>
              <option value="ALL">Wszystkie dostępne działy</option>
              {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </select>
          </label>
          <button type="button" className="dashboard-refresh" onClick={load}><i className="fas fa-sync-alt"></i> Odśwież</button>
        </div>
      </div>

      {error && <div className="dashboard-error"><i className="fas fa-exclamation-triangle"></i>{error}</div>}

      <div className="dashboard-kpi-grid">
        <Kpi label="Obsada dzisiaj" value={`${todayData.assignedCount} / ${todayData.demandCount}`} detail={todayData.openShiftCount > 0 ? `${todayData.openShiftCount} zmian bez obsady` : "brak otwartych zmian"} icon="fas fa-users" tone={todayData.openShiftCount > 0 ? "danger" : "good"} />
        <Kpi label="Obsada jutro" value={`${tomorrowData.assignedCount} / ${tomorrowData.demandCount}`} detail={tomorrowData.openShiftCount > 0 ? `${tomorrowData.openShiftCount} zmian bez obsady` : "plan obsady kompletny"} icon="fas fa-user-check" tone={tomorrowData.openShiftCount > 0 ? "warning" : "purple"} />
        <Kpi label="Wnioski oczekujące" value={pendingRequests} detail="w wybranym zakresie i działach" icon="fas fa-inbox" tone={pendingRequests > 0 ? "warning" : "purple"} />
        <Kpi label="RCP wymagające uwagi" value={missingRcpCount} detail={`${lateCount} spóźnień`} icon="fas fa-clock" tone={missingRcpCount > 0 ? "danger" : "purple"} />
      </div>

      <section className="dashboard-section">
        <div className="dashboard-section-heading">
          <div><span>Plan operacyjny</span><h2>Dzisiaj i jutro</h2></div>
          {lastRefresh && <small>Aktualizacja: {lastRefresh.toLocaleTimeString("pl-PL", {hour: "2-digit", minute: "2-digit"})}</small>}
        </div>
        <div className="dashboard-day-grid">
          <DayPlan title="Dzisiaj" date={today} dayData={todayData} />
          <DayPlan title="Jutro" date={tomorrow} dayData={tomorrowData} compactActual />
        </div>
      </section>

      <div className="dashboard-two-column">
        <section className="dashboard-panel">
          <div className="dashboard-panel-heading"><div><span>Realizacja założeń</span><h2>Plan vs wykonanie dzisiaj</h2></div><i className="fas fa-bullseye"></i></div>
          <ProgressLine label="Obsada" actual={todayData.assignedCount} planned={todayData.demandCount} />
          <ProgressLine label="Roboczogodziny" actual={todayData.actualHours} planned={todayData.plannedHours} suffix=" h" />
          <ProgressLine label="Sprzedaż" actual={todayData.actualSales} planned={todayData.plannedSales} />
          <div className="dashboard-inline-metrics">
            <span><small>SPMH plan</small><strong>{todayData.spmhPlan == null ? "—" : `${formatNumber(todayData.spmhPlan)} zł/h`}</strong></span>
            <span><small>SPMH wykonanie</small><strong>{todayData.spmhActual == null ? "—" : `${formatNumber(todayData.spmhActual)} zł/h`}</strong></span>
            <span><small>%COL plan</small><strong>{percent(todayData.colPlanPercent)}</strong></span>
            <span><small>%COL wykonanie</small><strong>{percent(todayData.colActualPercent)}</strong></span>
          </div>
        </section>

        <section className="dashboard-panel">
          <div className="dashboard-panel-heading"><div><span>Priorytety</span><h2>Wymaga uwagi</h2></div><i className="fas fa-exclamation-circle"></i></div>
          <div className="dashboard-attention-list">
            {attentionItems.length === 0 ? (
              <div className="dashboard-attention-empty"><i className="fas fa-check-circle"></i><div><strong>Brak krytycznych problemów</strong><span>Obsada i bieżące procesy nie wymagają pilnej reakcji.</span></div></div>
            ) : attentionItems.map((item, index) => (
              <button key={`${item.text}-${index}`} type="button" className={`dashboard-attention dashboard-attention-${item.tone}`} onClick={() => navigate(item.path)}>
                <i className={item.icon}></i><span>{item.text}</span><i className="fas fa-chevron-right"></i>
              </button>
            ))}
          </div>
        </section>
      </div>

      <section className="dashboard-section">
        <div className="dashboard-section-heading"><div><span>Szczegóły</span><h2>Sytuacja w działach</h2></div></div>
        {visibleReports.length > 0 ? <DepartmentDayTable reports={visibleReports} today={today} tomorrow={tomorrow} /> : <div className="dashboard-empty">Brak danych dla wybranego działu.</div>}
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-heading"><div><span>Skróty</span><h2>Najważniejsze funkcje</h2></div></div>
        <div className="dashboard-actions-grid">
          {actions.map(action => <QuickAction key={action.path} {...action} onClick={() => navigate(action.path)} />)}
        </div>
      </section>
    </>
  );
};

const EmployeeDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [requests, setRequests] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const today = useMemo(() => toDateString(new Date()), []);
  const tomorrow = useMemo(() => toDateString(addDays(new Date(), 1)), []);
  const weekEnd = useMemo(() => toDateString(addDays(new Date(), 7)), []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [scheduleResult, requestResult, openShiftResult, notificationResult] = await Promise.allSettled([
        axiosPrivate.get("/employee-work-schedule", {params: {dateFrom: today, dateTo: weekEnd}}),
        axiosPrivate.get("/schedule-change-requests/mine"),
        axiosPrivate.get("/schedule-change-requests/open-shifts", {params: {dateFrom: today, dateTo: weekEnd}}),
        axiosPrivate.get("/schedule-notifications/mine/unread-count")
      ]);
      setSchedule(scheduleResult.status === "fulfilled" ? scheduleResult.value.data : null);
      setRequests(requestResult.status === "fulfilled" ? requestResult.value.data || [] : []);
      setOpenShifts(openShiftResult.status === "fulfilled" ? openShiftResult.value.data || [] : []);
      setUnreadCount(notificationResult.status === "fulfilled" ? number(notificationResult.value.data?.count) : 0);
      setLoading(false);
    };
    load();
  }, [axiosPrivate, today, weekEnd]);

  const shifts = schedule?.shifts || [];
  const todayShifts = shifts.filter(shift => shift.startsAt?.slice(0, 10) === today);
  const tomorrowShifts = shifts.filter(shift => shift.startsAt?.slice(0, 10) === tomorrow);
  const pendingRequests = requests.filter(request => ["PENDING", "AWAITING_TARGET"].includes(request.status)).length;
  const nextShift = [...shifts].sort((a, b) => String(a.startsAt).localeCompare(String(b.startsAt)))[0];

  const ShiftList = ({items, emptyText}) => items.length === 0 ? <div className="dashboard-shift-empty">{emptyText}</div> : (
    <div className="dashboard-shift-list">
      {items.map(shift => (
        <div className="dashboard-shift" key={shift.assignmentId}>
          <span className="dashboard-shift-time">{formatTime(shift.startsAt)}–{formatTime(shift.endsAt)}</span>
          <div><strong>{shift.positionName || "Zmiana"}</strong><small>{shift.departmentName || ""}</small></div>
        </div>
      ))}
    </div>
  );

  if (loading) return <div className="dashboard-loading"><div className="dashboard-spinner"></div><span>Pobieranie Twojego planu…</span></div>;

  return (
    <>
      <div className="dashboard-toolbar dashboard-toolbar-employee">
        <div><span className="dashboard-eyebrow">Twój dzień pracy</span><h1>{schedule?.employeeFullName ? `Dzień dobry, ${schedule.employeeFullName}` : "Twój dashboard"}</h1><p>Grafik, wnioski, otwarte zmiany i najważniejsze informacje w jednym miejscu.</p></div>
      </div>

      <div className="dashboard-kpi-grid">
        <Kpi label="Dzisiaj" value={todayShifts.length ? `${todayShifts.length} ${todayShifts.length === 1 ? "zmiana" : "zmiany"}` : "Wolne"} detail={todayShifts.length ? `${formatTime(todayShifts[0].startsAt)}–${formatTime(todayShifts[todayShifts.length - 1].endsAt)}` : "brak zaplanowanej pracy"} icon="fas fa-calendar-day" tone="purple" />
        <Kpi label="Jutro" value={tomorrowShifts.length ? `${tomorrowShifts.length} ${tomorrowShifts.length === 1 ? "zmiana" : "zmiany"}` : "Wolne"} detail={tomorrowShifts.length ? `${formatTime(tomorrowShifts[0].startsAt)}–${formatTime(tomorrowShifts[tomorrowShifts.length - 1].endsAt)}` : "brak zaplanowanej pracy"} icon="fas fa-calendar-check" tone="purple" />
        <Kpi label="Powiadomienia" value={unreadCount} detail="nieprzeczytanych" icon="fas fa-bell" tone={unreadCount > 0 ? "warning" : "purple"} />
        <Kpi label="Wnioski w toku" value={pendingRequests} detail={`${openShifts.length} otwartych zmian w najbliższych 7 dniach`} icon="fas fa-exchange-alt" tone={pendingRequests > 0 ? "warning" : "purple"} />
      </div>

      <div className="dashboard-two-column">
        <section className="dashboard-panel">
          <div className="dashboard-panel-heading"><div><span>Dzisiaj</span><h2>{formatDay(today)}</h2></div><i className="fas fa-calendar-day"></i></div>
          <ShiftList items={todayShifts} emptyText="Dzisiaj nie masz zaplanowanej zmiany." />
        </section>
        <section className="dashboard-panel">
          <div className="dashboard-panel-heading"><div><span>Jutro</span><h2>{formatDay(tomorrow)}</h2></div><i className="fas fa-calendar-check"></i></div>
          <ShiftList items={tomorrowShifts} emptyText="Jutro nie masz zaplanowanej zmiany." />
        </section>
      </div>

      {nextShift && (
        <section className="dashboard-next-shift">
          <span className="dashboard-next-icon"><i className="fas fa-forward"></i></span>
          <div><small>Najbliższa zmiana</small><strong>{formatDay(nextShift.startsAt.slice(0, 10))}, {formatTime(nextShift.startsAt)}–{formatTime(nextShift.endsAt)}</strong><span>{nextShift.departmentName} · {nextShift.positionName}</span></div>
          <button type="button" onClick={() => navigate("/app/my-schedule")}>Otwórz mój grafik</button>
        </section>
      )}

      <section className="dashboard-section">
        <div className="dashboard-section-heading"><div><span>Skróty</span><h2>Najważniejsze funkcje</h2></div></div>
        <div className="dashboard-actions-grid dashboard-actions-employee">
          <QuickAction icon="fas fa-calendar-alt" title="Mój grafik" description="Zmiany, wnioski i zamiany" onClick={() => navigate("/app/my-schedule")} />
          <QuickAction icon="fas fa-bell" title="Powiadomienia" description="Publikacje i zmiany grafiku" onClick={() => navigate("/app/notifications")} />
          <QuickAction icon="fas fa-user" title="Moje dane" description="Kartoteka i dane pracownika" onClick={() => navigate("/app/employee-profile")} />
          <QuickAction icon="fas fa-book" title="Baza wiedzy" description="Dokumenty, instrukcje i pomoc" onClick={() => navigate("/app/knowledge-base")} />
        </div>
      </section>
    </>
  );
};

const GenericDashboard = () => {
  const navigate = useNavigate();
  return (
    <>
      <div className="dashboard-toolbar"><div><span className="dashboard-eyebrow">Qstaff</span><h1>Dashboard</h1><p>Szybki dostęp do funkcji dostępnych dla Twojego konta.</p></div></div>
      <section className="dashboard-section">
        <div className="dashboard-actions-grid dashboard-actions-employee">
          <QuickAction icon="fas fa-book" title="Baza wiedzy" description="Instrukcje, dokumenty i pomoc" onClick={() => navigate("/app/knowledge-base")} />
        </div>
      </section>
    </>
  );
};

const Dashboard = () => {
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles)
    ? auth.roles
    : typeof auth?.roles === "string"
      ? auth.roles.replace(/[\[\]\s]/g, "").split(",")
      : [];

  const isAdmin = roles.includes("ROLE_ADMIN");
  const isManager = roles.includes("ROLE_MANAGER");
  const isEmployee = roles.includes("ROLE_EMPLOYEE");
  const isMinion = roles.includes("ROLE_MINION");
  const hasEmployeeSelfService = isEmployee || isMinion;

  return (
    <div id="layoutSidenav_content" className="qstaff-dashboard-page">
      <div className="dashboard-shell">
        {(isAdmin || isManager) ? <ManagerDashboard isAdmin={isAdmin} /> : hasEmployeeSelfService ? <EmployeeDashboard /> : <GenericDashboard />}
      </div>
    </div>
  );
};

export default Dashboard;
